import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
	getOrderListRequest,
	getOrderSplitsRequest,
	getOrderItemsRequest,
	getSplitItemsBySplitIdRequest,
	getOrderReturnsRequest,
	getOrderReturnItemsRequest,
	getOrderDiscountsRequest,
	getOrderDiscountBreakdownRequest,
} from 'redux/reducers/orderManagement/reducer';
import {
	selectOrderListData,
	selectOrderListLoading,
	selectOrderSplitsData,
	selectOrderSplitsLoading,
	selectOrderItemsData,
	selectOrderItemsLoading,
	selectSplitItemsMap,
	selectSplitItemsLoadingMap,
} from 'redux/reducers/orderManagement/selectors';
import { Page } from 'layouts/Page';
import { OrderDetail } from 'components/OrderManagement/OrderDetail';
import { TFiltersQ4Us } from 'redux/types';

export const OrderManagementDetailPage: FC = () => {
	const { orderId } = useParams<{ orderId: string }>();
	const dispatch = useDispatch();
	const orderListData = useSelector(selectOrderListData);
	const orderListLoading = useSelector(selectOrderListLoading);
	const orderSplitsData = useSelector(selectOrderSplitsData);
	const orderSplitsLoading = useSelector(selectOrderSplitsLoading);
	const orderItemsData = useSelector(selectOrderItemsData);
	const orderItemsLoading = useSelector(selectOrderItemsLoading);
	const splitItemsMap = useSelector(selectSplitItemsMap);
	const splitItemsLoadingMap = useSelector(selectSplitItemsLoadingMap);

	const rawFirst = orderListData[0];
	const order = orderListLoading
		? null
		: rawFirst != null && String(rawFirst.id) === String(orderId ?? '')
			? rawFirst
			: null;

	useEffect(() => {
		if (!orderId) return;
		const id = Number(orderId);
		dispatch(
			getOrderListRequest({
				tableName: 'order_info_view_with_s10',
				orderBy: ['id desc'],
				filter: [{ name: 'id', value: orderId, comparator: '=' }],
				page: { size: 1, from: 0 },
			}),
		);
		dispatch(getOrderSplitsRequest(id));
		dispatch(getOrderItemsRequest(id));
		dispatch(
			getOrderReturnsRequest({
				filter: [{ name: 'order_id', value: orderId }],
				orderBy: ['id'],
				page: { size: 1000, from: 0 },
				tableName: 'dashboard_order_return_view',
			}),
		);
		dispatch(
			getOrderReturnItemsRequest({
				filter: [{ name: 'order_id', value: orderId }],
				orderBy: ['return_id'],
				page: { size: 1000, from: 0 },
				tableName: 'dashboard_order_return_breakdown_view',
			}),
		);
		dispatch(
			getOrderDiscountsRequest({
				filter: [{ name: 'order_id', value: orderId }],
				orderBy: ['id'],
				page: { size: 1000, from: 0 },
				tableName: 'dashboard_order_discount_view',
			}),
		);
		dispatch(
			getOrderDiscountBreakdownRequest({
				filter: [{ name: 'order_id', value: orderId }],
				orderBy: ['discount_id desc, timestamp_year desc'],
				page: { size: 1000, from: 0 },
				tableName: 'dashboard_order_discount_breakdown_view',
			}),
		);
	}, [dispatch, orderId]);

	useEffect(() => {
		if (!orderSplitsData.length) return;
		orderSplitsData.forEach((split) => {
			if (!splitItemsMap[split.split_id]) {
				dispatch(getSplitItemsBySplitIdRequest(split.split_id));
			}
		});
	}, [dispatch, orderSplitsData]);

	return (
		<Page title={`Order ${orderId ?? ''} – EAS Control Center`}>
			<OrderDetail
				order={order}
				loading={orderListLoading}
				orderSplits={orderSplitsData}
				orderSplitsLoading={orderSplitsLoading}
				orderItems={orderItemsData}
				orderItemsLoading={orderItemsLoading}
				splitItemsMap={splitItemsMap}
				splitItemsLoadingMap={splitItemsLoadingMap}
			/>
		</Page>
	);
};
